import type { ApiEngagementMessage } from "@bridge-applications/pb-types";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ButtonVar1 } from "../../../../../../common/ButtonVar1/ButtonVar1";
import { formatCurrencyMinorUnits } from "../../../../../../../logic/helpers/currencyAmount";
import { haptics } from "../../../../../../../logic/helpers/haptics";
import { useInboxMessageTime } from "../../../../../../../logic/hooks/useInboxMessageTime";
import energyCan from "../../../../../../../assets/static/images/inbox/energy-can-fat-outlined.png";
import spinWheel from "../../../../../../../assets/static/images/inbox/spin-wheel-icon.png";
import beachCoins from "../../../../../../../assets/static/images/holy-wheel/prizes/hw__po__coins.png";
import {
    InboxMessageCard,
    type InboxMessageVariant,
} from "../InboxMessageCard/InboxMessageCard";
import "./EngagementMessage.scss";

interface EngagementMessageProps {
    message: ApiEngagementMessage;
    onClaim?: (messageId: string) => Promise<ApiEngagementMessage>;
    /** Override the automatic result / gift / notice presentation when needed. */
    variant?: InboxMessageVariant;
}

export function EngagementMessage({
    message,
    onClaim,
    variant,
}: EngagementMessageProps) {
    const { t, i18n } = useTranslation();
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const claimingRef = useRef(false);
    const locale = i18n.resolvedLanguage ?? "en";
    const deadlines = [
        message.expiresAt,
        message.rewardState === "CLAIMABLE" ? message.claimExpiresAt : null,
    ].filter((value): value is string => value !== null);
    const deadline =
        deadlines.sort((a, b) => Date.parse(a) - Date.parse(b))[0] ?? null;
    const { ageLabel, countdown, deadlineReached } = useInboxMessageTime(
        message.createdAt,
        deadline,
        locale,
    );
    const presentation =
        variant ??
        (message.outcome === "CORRECT"
            ? "correct"
            : message.outcome === "INCORRECT"
              ? "incorrect"
              : message.rewards.length > 0
                ? "gift"
                : "generic");
    const hasUnclaimedReward =
        message.rewards.length > 0 && message.rewardState === "CLAIMABLE";
    const isClaimed =
        message.rewards.length > 0 && message.rewardState === "CLAIMED";
    const canClaim =
        hasUnclaimedReward && !deadlineReached && onClaim !== undefined;
    const showRewardAction = canClaim || isClaimed;
    const text = (copy: Record<string, string>) =>
        copy[locale] ?? copy[locale.split("-")[0] ?? "en"] ?? copy.en ?? "";
    const rewardLabel = (reward: ApiEngagementMessage["rewards"][number]) => {
        const amount = new Intl.NumberFormat(locale).format(
            BigInt(reward.amount),
        );
        if (reward.kind === "SM_ENERGY")
            return t("commerce.rewards.energy", { amount });
        if (reward.kind === "CURRENCY")
            return formatCurrencyMinorUnits(
                reward.amount,
                reward.currencyCode,
                locale,
            );
        if (reward.kind === "WHEEL_SPINS")
            return `${amount} × ${t("holyWheel.spin")}`;
        return t("commerce.rewards.item", { amount });
    };
    const singleReward =
        message.rewards.length === 1 ? message.rewards[0] : undefined;
    const rewardIcon =
        singleReward?.kind === "SM_ENERGY"
            ? energyCan
            : singleReward?.kind === "WHEEL_SPINS"
              ? spinWheel
              : singleReward?.kind === "CURRENCY" &&
                  singleReward.currencyCode === "BEACH_COINS"
                ? beachCoins
                : null;
    const rewardAmount =
        singleReward && rewardIcon
            ? new Intl.NumberFormat(locale).format(BigInt(singleReward.amount))
            : null;
    const summary = singleReward
        ? rewardLabel(singleReward)
        : t("inbox.rewardsIncluded", { count: message.rewards.length });
    const claim = async () => {
        if (isClaimed) {
            haptics.notification("error");
            return;
        }
        if (claimingRef.current || !canClaim || !onClaim) return;
        claimingRef.current = true;
        setBusy(true);
        setError(null);
        try {
            await onClaim(message.id);
        } catch {
            setError(t("inbox.errors.claim"));
        } finally {
            claimingRef.current = false;
            setBusy(false);
        }
    };

    return (
        <div className="engagement-message">
            <InboxMessageCard
                title={text(message.title)}
                body={text(message.body)}
                variant={presentation}
                createdAt={message.createdAt}
                ageLabel={ageLabel}
                countdown={countdown}
                countdownLabel={
                    countdown
                        ? t("inbox.expiresIn", { time: countdown })
                        : undefined
                }
                isRead={message.readAt !== null && !hasUnclaimedReward}
                rewardAction={
                    showRewardAction ? (
                        <ButtonVar1
                            className={[
                                "engagement-message__claim",
                                rewardAmount === null || rewardAmount.length > 6
                                    ? "engagement-message__claim--text"
                                    : undefined,
                            ]
                                .filter(Boolean)
                                .join(" ")}
                            type="button"
                            disabled={busy}
                            aria-disabled={isClaimed}
                            data-reward-state={message.rewardState}
                            data-reward-kind={singleReward?.kind}
                            aria-busy={busy}
                            aria-label={
                                isClaimed
                                    ? `${t("inbox.claimed")}: ${summary}`
                                    : busy
                                      ? t("inbox.claiming")
                                      : t("inbox.claimReward", {
                                            reward: summary,
                                        })
                            }
                            onClick={() => void claim()}
                            content={
                                <span
                                    className="engagement-message__claim-content"
                                    aria-hidden="true"
                                >
                                    {rewardIcon && (
                                        <img
                                            className="engagement-message__claim-icon"
                                            src={rewardIcon}
                                            alt=""
                                            draggable={false}
                                        />
                                    )}
                                    <span
                                        className="engagement-message__claim-label"
                                        dir={
                                            rewardAmount !== null
                                                ? "ltr"
                                                : undefined
                                        }
                                    >
                                        {rewardAmount ??
                                            t(
                                                isClaimed
                                                    ? "inbox.claimed"
                                                    : "inbox.claim",
                                            )}
                                    </span>
                                </span>
                            }
                        />
                    ) : undefined
                }
            >
                {message.rewards.length > 0 &&
                    (!showRewardAction || rewardAmount === null) && (
                        <ul className="engagement-message__rewards">
                            {message.rewards.map((reward) => (
                                <li key={reward.key}>{rewardLabel(reward)}</li>
                            ))}
                        </ul>
                    )}
                {message.rewardState === "CLAIMED" && (
                    <span
                        className="engagement-message__announcement"
                        role="status"
                    >
                        {t("inbox.claimed")}
                    </span>
                )}
                {(message.rewardState === "EXPIRED" ||
                    (message.rewardState === "CLAIMABLE" &&
                        deadlineReached)) && (
                    <p className="engagement-message__status" role="status">
                        {t("inbox.rewardExpired")}
                    </p>
                )}
                {busy && (
                    <span
                        className="engagement-message__announcement"
                        role="status"
                    >
                        {t("inbox.claiming")}
                    </span>
                )}
                {error && (
                    <p className="engagement-message__error" role="alert">
                        {error}
                    </p>
                )}
            </InboxMessageCard>
        </div>
    );
}
